import React, { useState, useReducer, useMemo, useEffect } from "react";
import { Box, Stack, Typography, IconButton, Tooltip, Checkbox, Button, InputBase, Select, MenuItem } from "@mui/material";
import {
    IconChevronRight,
    IconChevronDown,
    IconTrash,
    IconCopy,
    IconCheck,
    IconRefresh,
    IconFilePlus,
    IconUpload,
    IconX,
    IconBraces,
} from "@tabler/icons-react";

const TYPE_COLORS = {
    string: "#2E7D32",
    number: "#B26A00",
    integer: "#C65102",
    boolean: "#7B1FA2",
    object: "#1565C0",
    array: "#AD1457",
    null: "#616161",
    anyOf: "#E65100",
};

const TYPE_OPTIONS = ["string", "number", "integer", "boolean", "object", "array", "null", "anyOf"];
const FORMAT_OPTIONS = ["", "email", "date", "date-time", "time", "uri", "uuid", "hostname", "ipv4", "ipv6", "regex", "duration"];

let __id = 0;
const genId = () => "n" + __id++;

function createDefaultNode(type, description = "") {
    const base = { id: genId(), type, description };
    switch (type) {
        case "object":
            return { ...base, children: [], required: [], additionalProperties: false };
        case "array":
            return { ...base, items: createDefaultNode("string") };
        case "anyOf":
            return { ...base, variants: [createDefaultNode("object"), createDefaultNode("object")] };
        case "string":
            return { ...base, format: "", pattern: "", minLength: "", maxLength: "", enum: [] };
        case "number":
        case "integer":
            return { ...base, minimum: "", maximum: "", enum: [] };
        default:
            return base;
    }
}

function blankState() {
    return createDefaultNode("object");
}

function demoState() {
    const nameNode = createDefaultNode("string", "The name of the user");
    const usernameNode = { ...createDefaultNode("string", "The username. Must start with @"), pattern: "^@[a-zA-Z0-9_]+$" };
    const emailNode = { ...createDefaultNode("string", "The email of the user"), format: "email" };
    return {
        ...createDefaultNode("object"),
        children: [
            { key: "name", node: nameNode },
            { key: "username", node: usernameNode },
            { key: "email", node: emailNode },
        ],
        required: ["name", "username", "email"],
        additionalProperties: false,
    };
}

function setNodeAtPath(node, path, updater) {
    if (path.length === 0) return updater(node);
    const [step, ...rest] = path;
    if (step.kind === "prop") {
        return {
            ...node,
            children: node.children.map((c) => (c.node.id === step.id ? { ...c, node: setNodeAtPath(c.node, rest, updater) } : c)),
        };
    }
    if (step.kind === "items") {
        return { ...node, items: setNodeAtPath(node.items, rest, updater) };
    }
    if (step.kind === "variant") {
        return { ...node, variants: node.variants.map((v) => (v.id === step.id ? setNodeAtPath(v, rest, updater) : v)) };
    }
    return node;
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_FIELD":
            return setNodeAtPath(state, action.path, (n) => ({ ...n, [action.field]: action.value }));

        case "CHANGE_TYPE":
            return setNodeAtPath(state, action.path, (n) => ({ ...createDefaultNode(action.newType, n.description), id: n.id }));

        case "ADD_PROPERTY":
            return setNodeAtPath(state, action.path, (n) => {
                let key = "propertyName";
                let i = 1;
                while (n.children.some((c) => c.key === key)) key = "propertyName" + i++;
                return { ...n, children: [...n.children, { key, node: createDefaultNode("string") }] };
            });

        case "DELETE_PROPERTY":
            return setNodeAtPath(state, action.path, (n) => {
                const entry = n.children.find((c) => c.node.id === action.id);
                return { ...n, children: n.children.filter((c) => c.node.id !== action.id), required: n.required.filter((k) => k !== entry?.key) };
            });

        case "RENAME_PROPERTY":
            return setNodeAtPath(state, action.path, (n) => {
                const entry = n.children.find((c) => c.node.id === action.id);
                const oldKey = entry?.key;
                const children = n.children.map((c) => (c.node.id === action.id ? { ...c, key: action.newKey } : c));
                const required = n.required.map((k) => (k === oldKey ? action.newKey : k));
                return { ...n, children, required };
            });

        case "TOGGLE_REQUIRED":
            return setNodeAtPath(state, action.path, (n) => ({
                ...n,
                required: n.required.includes(action.key) ? n.required.filter((k) => k !== action.key) : [...n.required, action.key],
            }));

        case "SET_ADDITIONAL_PROPERTIES":
            return setNodeAtPath(state, action.path, (n) => ({ ...n, additionalProperties: action.value }));

        case "ADD_VARIANT":
            return setNodeAtPath(state, action.path, (n) => ({ ...n, variants: [...n.variants, createDefaultNode("object")] }));

        case "DELETE_VARIANT":
            return setNodeAtPath(state, action.path, (n) => ({ ...n, variants: n.variants.filter((v) => v.id !== action.id) }));

        case "LOAD":
            return action.state;

        default:
            return state;
    }
}

function buildSchema(node) {
    if (node.type === "anyOf") {
        const out = { anyOf: node.variants.map(buildSchema) };
        if (node.description) out.description = node.description;
        return out;
    }

    const out = { type: node.type };
    if (node.description) out.description = node.description;

    if (node.type === "object") {
        out.properties = {};
        node.children.forEach((c) => {
            out.properties[c.key] = buildSchema(c.node);
        });
        out.additionalProperties = !!node.additionalProperties;
        if (node.required.length) out.required = [...node.required];
    } else if (node.type === "array") {
        out.items = buildSchema(node.items);
    } else if (node.type === "string") {
        if (node.format) out.format = node.format;
        if (node.pattern) out.pattern = node.pattern;
        if (node.minLength !== "") out.minLength = Number(node.minLength);
        if (node.maxLength !== "") out.maxLength = Number(node.maxLength);
        if (node.enum.length) out.enum = node.enum;
    } else if (node.type === "number" || node.type === "integer") {
        if (node.minimum !== "") out.minimum = Number(node.minimum);
        if (node.maximum !== "") out.maximum = Number(node.maximum);
        if (node.enum.length) out.enum = node.enum.map(Number);
    }
    return out;
}

function parseNode(s) {
    if (!s || typeof s !== "object") return createDefaultNode("string");

    if (Array.isArray(s.anyOf)) {
        const base = createDefaultNode("anyOf", s.description || "");
        if (s.anyOf.length) base.variants = s.anyOf.map(parseNode);
        return base;
    }

    const type = TYPE_OPTIONS.includes(s.type) && s.type !== "anyOf" ? s.type : "string";
    const base = createDefaultNode(type, s.description || "");

    if (type === "object") {
        const props = s.properties || {};
        base.children = Object.keys(props).map((k) => ({ key: k, node: parseNode(props[k]) }));
        base.required = Array.isArray(s.required) ? [...s.required] : [];
        base.additionalProperties = !!s.additionalProperties;
    } else if (type === "array") {
        base.items = s.items ? parseNode(s.items) : createDefaultNode("string");
    } else if (type === "string") {
        base.format = s.format || "";
        base.pattern = s.pattern || "";
        base.minLength = s.minLength ?? "";
        base.maxLength = s.maxLength ?? "";
        base.enum = Array.isArray(s.enum) ? s.enum : [];
    } else if (type === "number" || type === "integer") {
        base.minimum = s.minimum ?? "";
        base.maximum = s.maximum ?? "";
        base.enum = Array.isArray(s.enum) ? s.enum : [];
    }
    return base;
}

function parseInitial(raw) {
    try {
        const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (!obj || typeof obj !== "object") return null;
        return parseNode(obj);
    } catch (e) {
        return null;
    }
}

function extendPath(path, segment) {
    return path ? `${path}.${segment}` : segment;
}

function validateRoot(root) {
    const warnings = [];
    const walk = (node, path) => {
        const location = path ? `"${path}"` : "the top level";
        if (node.type === "object") {
            if (node.children.length === 0) warnings.push(`The object at ${location} has no properties yet - add at least one.`);
            const seen = new Set();
            node.children.forEach((c) => {
                if (!c.key || !c.key.trim()) warnings.push(`A property at ${location} has no name - give it one.`);
                else if (seen.has(c.key)) warnings.push(`Two properties at ${location} are both named "${c.key}" - rename one.`);
                seen.add(c.key);
                walk(c.node, extendPath(path, c.key || "?"));
            });
        } else if (node.type === "array") {
            walk(node.items, extendPath(path, "items"));
        } else if (node.type === "anyOf") {
            if (node.variants.length < 2) warnings.push(`The anyOf at ${location} has only ${node.variants.length} variant - add at least one more, or switch it to a regular type.`);
            node.variants.forEach((v, i) => walk(v, extendPath(path, `variant ${i + 1}`)));
        }
    };
    walk(root, "");
    return warnings;
}

const TOKEN_COLORS = { key: "#1565C0", string: "#2E7D32", number: "#B26A00", boolean: "#7B1FA2", null: "#757575", punct: "#9E9E9E", plain: "#424242" };

function tokenizeJSON(str) {
    const regex = /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?|[{}[\],])/g;
    const tokens = [];
    let last = 0;
    let m;
    while ((m = regex.exec(str)) !== null) {
        if (m.index > last) tokens.push({ text: str.slice(last, m.index), cls: "plain" });
        const match = m[0];
        let cls = "number";
        if (/^"/.test(match)) cls = /:$/.test(match) ? "key" : "string";
        else if (/true|false/.test(match)) cls = "boolean";
        else if (/null/.test(match)) cls = "null";
        else if (/^[{}[\],]$/.test(match)) cls = "punct";
        tokens.push({ text: match, cls });
        last = regex.lastIndex;
    }
    if (last < str.length) tokens.push({ text: str.slice(last), cls: "plain" });
    return tokens;
}

// Компактни MUI InputBase/Select стилови кои визуелно ги заменуваат старите
// raw <input>/<select> елементи, но сега автоматски земаат fontFamily: "Roboto"
// од темата (MuiInputBase / MuiSelect style overrides), без hardcode-ирање.
const inputBoxSx = {
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "6px",
    px: 1,
    "&:focus-within": { borderColor: "text.secondary" },
    "& .MuiInputBase-input": { fontSize: 13, py: 0.6 },
};

const subtleBoxSx = {
    bgcolor: "transparent",
    borderBottom: "1px solid",
    borderColor: "divider",
    "&:focus-within": { borderColor: "primary.main" },
    "& .MuiInputBase-input": { fontSize: 12, py: 0.4 },
};

function Field({ label, width, children }) {
    return (
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ width }}>
            <Typography sx={{ fontSize: 10, textTransform: "uppercase", fontWeight: 600, color: "text.disabled", whiteSpace: "nowrap" }}>{label}</Typography>
            {children}
        </Stack>
    );
}

function EnumEditor({ values, onChange }) {
    const [text, setText] = useState("");
    const add = () => {
        const v = text.trim();
        if (!v) return;
        onChange([...values, v]);
        setText("");
    };
    return (
        <Stack direction="row" flexWrap="wrap" alignItems="center" gap={0.75} sx={{ flex: 1, minWidth: 180 }}>
            {values.map((v, i) => (
                <Stack
                    key={i}
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ fontSize: 12, px: 1, py: 0.4, borderRadius: 8, bgcolor: "action.hover", border: "1px solid", borderColor: "divider", color: "text.primary" }}
                >
                    <span>{v}</span>
                    <Box component="button" onClick={() => onChange(values.filter((_, idx) => idx !== i))} sx={{ all: "unset", cursor: "pointer", opacity: 0.6, display: "flex", "&:hover": { opacity: 1 } }}>
                        <IconX size={10} />
                    </Box>
                </Stack>
            ))}
            <InputBase
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        add();
                    }
                }}
                onBlur={add}
                placeholder="add value ↵"
                sx={{ ...subtleBoxSx, width: 100 }}
            />
        </Stack>
    );
}

function NodeEditor({ node, path, keyName, keyless, isRequired, dispatch, depth, onDelete, onRename, onToggleRequired }) {
    const [collapsed, setCollapsed] = useState(false);
    const hasChildren = node.type === "object" || node.type === "array" || node.type === "anyOf";
    const color = TYPE_COLORS[node.type];

    return (
        <Box
            sx={
                hasChildren
                    ? { borderRadius: 2, bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }
                    : { borderBottom: "1px solid", borderColor: "divider", transition: "background-color 0.15s", "&:hover": { bgcolor: "action.hover" }, "&:last-child": { borderBottom: "none" } }
            }
        >
            <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ px: hasChildren ? 2 : 0.5, py: 1.75 }}>
                {hasChildren ? (
                    <IconButton size="small" onClick={() => setCollapsed((c) => !c)} sx={{ color: "text.secondary", p: 0.25 }}>
                        {collapsed ? <IconChevronRight size={14} /> : <IconChevronDown size={14} />}
                    </IconButton>
                ) : (
                    <Box sx={{ width: 20 }} />
                )}

                {!keyless && (
                    <InputBase
                        value={keyName}
                        onChange={(e) => onRename(e.target.value)}
                        spellCheck={false}
                        placeholder="propertyName"
                        sx={{ ...inputBoxSx, width: 150, "& .MuiInputBase-input": { ...inputBoxSx["& .MuiInputBase-input"], fontWeight: 500 } }}
                    />
                )}

                <Select
                    value={node.type}
                    onChange={(e) => dispatch({ type: "CHANGE_TYPE", path, newType: e.target.value })}
                    size="small"
                    variant="standard"
                    disableUnderline
                    sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        textTransform: "capitalize",
                        borderRadius: "6px",
                        bgcolor: color + "18",
                        color,
                        border: `1px solid ${color}55`,
                        "& .MuiSelect-select": { py: 0.75, px: 1.25 },
                    }}
                >
                    {TYPE_OPTIONS.map((t) => (
                        <MenuItem key={t} value={t} sx={{ fontSize: 12, textTransform: "capitalize" }}>
                            {t}
                        </MenuItem>
                    ))}
                </Select>

                {!keyless && (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: "text.secondary", fontSize: 12, cursor: "pointer" }} component="label">
                        <Checkbox size="small" checked={isRequired} onChange={onToggleRequired} sx={{ p: 0.25 }} />
                        required
                    </Stack>
                )}

                <Box sx={{ flex: 1 }} />

                {onDelete && (
                    <Tooltip title={keyless ? "Delete variant" : "Delete property"}>
                        <IconButton size="small" onClick={onDelete} sx={{ color: "error.main", opacity: 0.75, "&:hover": { opacity: 1 } }}>
                            <IconTrash size={14} />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            <Stack spacing={2} sx={{ px: hasChildren ? 2 : 0.5, pb: hasChildren ? 2 : 1.75 }}>
                <InputBase
                    value={node.description}
                    onChange={(e) => dispatch({ type: "SET_FIELD", path, field: "description", value: e.target.value })}
                    placeholder="Description (shown to the model)"
                    sx={{ ...subtleBoxSx, width: "100%", color: "text.secondary" }}
                />

                {node.type === "string" && (
                    <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={2.5} rowGap={1.25}>
                        <Field label="format" width={100}>
                            <Select
                                value={node.format}
                                onChange={(e) => dispatch({ type: "SET_FIELD", path, field: "format", value: e.target.value })}
                                size="small"
                                variant="standard"
                                disableUnderline
                                sx={{ fontSize: 12, width: "100%", "& .MuiSelect-select": { py: 0.4 } }}
                            >
                                {FORMAT_OPTIONS.map((f) => (
                                    <MenuItem key={f} value={f} sx={{ fontSize: 12 }}>
                                        {f || "—"}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Field>
                        <Field label="pattern" width={140}>
                            <InputBase
                                value={node.pattern}
                                onChange={(e) => dispatch({ type: "SET_FIELD", path, field: "pattern", value: e.target.value })}
                                placeholder="^[a-z]+$"
                                sx={{ ...subtleBoxSx, width: "100%" }}
                            />
                        </Field>
                        <Field label="minLen" width={70}>
                            <InputBase
                                value={node.minLength}
                                onChange={(e) => dispatch({ type: "SET_FIELD", path, field: "minLength", value: e.target.value })}
                                sx={{ ...subtleBoxSx, width: "100%" }}
                            />
                        </Field>
                        <Field label="maxLen" width={70}>
                            <InputBase
                                value={node.maxLength}
                                onChange={(e) => dispatch({ type: "SET_FIELD", path, field: "maxLength", value: e.target.value })}
                                sx={{ ...subtleBoxSx, width: "100%" }}
                            />
                        </Field>
                        <Field label="enum">
                            <EnumEditor values={node.enum} onChange={(v) => dispatch({ type: "SET_FIELD", path, field: "enum", value: v })} />
                        </Field>
                    </Stack>
                )}

                {(node.type === "number" || node.type === "integer") && (
                    <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={2.5} rowGap={1.25}>
                        <Field label="minimum" width={80}>
                            <InputBase
                                value={node.minimum}
                                onChange={(e) => dispatch({ type: "SET_FIELD", path, field: "minimum", value: e.target.value })}
                                sx={{ ...subtleBoxSx, width: "100%" }}
                            />
                        </Field>
                        <Field label="maximum" width={80}>
                            <InputBase
                                value={node.maximum}
                                onChange={(e) => dispatch({ type: "SET_FIELD", path, field: "maximum", value: e.target.value })}
                                sx={{ ...subtleBoxSx, width: "100%" }}
                            />
                        </Field>
                        <Field label="enum">
                            <EnumEditor values={node.enum} onChange={(v) => dispatch({ type: "SET_FIELD", path, field: "enum", value: v })} />
                        </Field>
                    </Stack>
                )}

                {node.type === "object" && !collapsed && (
                    <Stack spacing={1.5} sx={{ pl: 2, ml: 0.75, pt: 1, borderLeft: "2px solid", borderColor: "divider" }}>
                        {node.children.map((entry) => (
                            <NodeEditor
                                key={entry.node.id}
                                node={entry.node}
                                path={[...path, { kind: "prop", id: entry.node.id }]}
                                keyName={entry.key}
                                keyless={false}
                                isRequired={node.required.includes(entry.key)}
                                dispatch={dispatch}
                                depth={depth + 1}
                                onDelete={() => dispatch({ type: "DELETE_PROPERTY", path, id: entry.node.id })}
                                onRename={(newKey) => dispatch({ type: "RENAME_PROPERTY", path, id: entry.node.id, newKey })}
                                onToggleRequired={() => dispatch({ type: "TOGGLE_REQUIRED", path, key: entry.key })}
                            />
                        ))}

                        <Button
                            onClick={() => dispatch({ type: "ADD_PROPERTY", path })}
                            size="small"
                            sx={{
                                alignSelf: "stretch",
                                justifyContent: "flex-start",
                                fontSize: 12,
                                fontWeight: 600,
                                px: 1,
                                py: 0.75,
                                borderRadius: "6px",
                                bgcolor: "transparent",
                                color: "text.secondary",
                                border: "1px dashed",
                                borderColor: "divider",
                                textTransform: "none",
                                "&:hover": { bgcolor: TYPE_COLORS.object + "0c", borderColor: TYPE_COLORS.object + "55", color: TYPE_COLORS.object },
                            }}
                        >
                            + Add property
                        </Button>

                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: "text.secondary", fontSize: 12, cursor: "pointer", pt: 0.5 }} component="label">
                            <Checkbox
                                size="small"
                                checked={node.additionalProperties}
                                onChange={(e) => dispatch({ type: "SET_ADDITIONAL_PROPERTIES", path, value: e.target.checked })}
                                sx={{ p: 0.25 }}
                            />
                            Allow additional properties
                        </Stack>
                    </Stack>
                )}

                {node.type === "array" && !collapsed && (
                    <Stack spacing={1.5} sx={{ pl: 2, ml: 0.75, pt: 1, borderLeft: "2px solid", borderColor: "divider" }}>
                        <Typography sx={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5, color: "text.disabled" }}>items</Typography>
                        <NodeEditor node={node.items} path={[...path, { kind: "items" }]} keyless={true} dispatch={dispatch} depth={depth + 1} />
                    </Stack>
                )}

                {node.type === "anyOf" && !collapsed && (
                    <Stack spacing={1.5} sx={{ pl: 2, ml: 0.75, pt: 1, borderLeft: "2px solid", borderColor: "divider" }}>
                        {node.variants.map((variant, idx) => (
                            <NodeEditor
                                key={variant.id}
                                node={variant}
                                path={[...path, { kind: "variant", id: variant.id }]}
                                keyless={true}
                                dispatch={dispatch}
                                depth={depth + 1}
                                onDelete={node.variants.length > 1 ? () => dispatch({ type: "DELETE_VARIANT", path, id: variant.id }) : undefined}
                            />
                        ))}
                        <Button
                            onClick={() => dispatch({ type: "ADD_VARIANT", path })}
                            size="small"
                            sx={{
                                alignSelf: "stretch",
                                justifyContent: "flex-start",
                                fontSize: 12,
                                fontWeight: 600,
                                px: 1,
                                py: 0.75,
                                borderRadius: "6px",
                                bgcolor: "transparent",
                                color: "text.secondary",
                                border: "1px dashed",
                                borderColor: "divider",
                                textTransform: "none",
                                "&:hover": { bgcolor: TYPE_COLORS.anyOf + "0c", borderColor: TYPE_COLORS.anyOf + "55", color: TYPE_COLORS.anyOf },
                            }}
                        >
                            + Add variant
                        </Button>
                    </Stack>
                )}
            </Stack>
        </Box>
    );
}

export default function JSONSchemaBuilder({ initialSchema, onChange, showPreview = true, showToolbar = true }) {
    const [state, dispatch] = useReducer(reducer, undefined, () => parseInitial(initialSchema) || blankState());
    const [copied, setCopied] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [importText, setImportText] = useState("");
    const [importError, setImportError] = useState("");

    const output = useMemo(() => buildSchema(state), [state]);
    const warnings = useMemo(() => validateRoot(state), [state]);
    const jsonStr = useMemo(() => JSON.stringify(output, null, 2), [output]);
    const tokens = useMemo(() => tokenizeJSON(jsonStr), [jsonStr]);

    useEffect(() => {
        onChange?.(output, warnings);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [output, warnings]);

    const copy = () => {
        navigator.clipboard.writeText(jsonStr);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const doImport = () => {
        const parsed = parseInitial(importText);
        if (!parsed) {
            setImportError("Couldn't parse that as JSON - check for a trailing comma or unmatched bracket.");
            return;
        }
        dispatch({ type: "LOAD", state: parsed });
        setImportOpen(false);
        setImportText("");
        setImportError("");
    };

    return (
        <Box sx={{ width: "100%", bgcolor: "background.default", color: "text.primary", borderRadius: 2 }}>
            {showToolbar && (
                <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1.5} sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ color: TYPE_COLORS.object, pr: 1 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: "text.primary" }}>Schema Builder</Typography>
                    </Stack>

                    <Box sx={{ flex: 1 }} />

                    <ToolbarButton icon={<IconUpload size={12} />} label="Import" onClick={() => setImportOpen((o) => !o)} />
                    <ToolbarButton icon={<IconFilePlus size={12} />} label="New" onClick={() => dispatch({ type: "LOAD", state: blankState() })} />
                    <ToolbarButton icon={<IconRefresh size={12} />} label="Example" onClick={() => dispatch({ type: "LOAD", state: demoState() })} />
                </Stack>
            )}

            {importOpen && (
                <Stack spacing={1} sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: "action.hover" }}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Paste a JSON Schema to load it into the builder.</Typography>
                    <InputBase
                        value={importText}
                        onChange={(e) => setImportText(e.target.value)}
                        multiline
                        minRows={6}
                        spellCheck={false}
                        placeholder='{ "type": "object", "properties": { ... } }'
                        sx={{ ...inputBoxSx, width: "100%", boxSizing: "border-box", alignItems: "flex-start", py: 1 }}
                    />
                    {importError && <Typography sx={{ fontSize: 12, color: "error.main" }}>{importError}</Typography>}
                    <Stack direction="row" spacing={1}>
                        <Button
                            onClick={doImport}
                            size="small"
                            sx={{ fontSize: 12, fontWeight: 700, px: 1.5, py: 0.6, borderRadius: "6px", bgcolor: TYPE_COLORS.object + "18", color: TYPE_COLORS.object, border: `1px solid ${TYPE_COLORS.object}55`, textTransform: "none" }}
                        >
                            Parse &amp; load
                        </Button>
                        <Button
                            onClick={() => {
                                setImportOpen(false);
                                setImportError("");
                            }}
                            size="small"
                            sx={{ fontSize: 12, fontWeight: 700, px: 1.5, py: 0.6, borderRadius: "6px", bgcolor: "transparent", color: "text.secondary", border: "1px solid", borderColor: "divider", textTransform: "none" }}
                        >
                            Cancel
                        </Button>
                    </Stack>
                </Stack>
            )}

            <Stack direction={{ xs: "column", md: showPreview ? "row" : "column" }} sx={{ minHeight: 0 }}>
                <Box sx={{ flex: 1, minWidth: 0, overflow: "auto", p: 3 }}>
                    <NodeEditor node={state} path={[]} keyless={true} dispatch={dispatch} depth={0} />
                </Box>

                {showPreview && (
                    <Box sx={{ flex: 1, minWidth: 0, flexShrink: 0, display: "flex", flexDirection: "column", borderLeft: { md: "1px solid" }, borderColor: "divider", borderTop: { xs: "1px solid", md: "none" }, bgcolor: "background.paper" }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                            <Typography sx={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5, color: "text.disabled" }}>json output</Typography>
                            <Button
                                onClick={copy}
                                size="small"
                                startIcon={copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                                sx={{ fontSize: 12, fontWeight: 700, px: 1, py: 0.4, borderRadius: "6px", bgcolor: "background.default", border: "1px solid", borderColor: "divider", color: copied ? TYPE_COLORS.string : "text.secondary", textTransform: "none" }}
                            >
                                {copied ? "copied" : "copy"}
                            </Button>
                        </Stack>
                        {/* JSON preview останува во monospace (не Roboto/Poppins) - намерно, за да се разликува код-содржината од UI текстот */}
                        <Box component="pre" sx={{ flex: 1, overflow: "auto", p: 1.5, m: 0, fontSize: 12, lineHeight: 1.6, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {tokens.map((t, i) => (
                                <Box component="span" key={i} sx={{ color: TOKEN_COLORS[t.cls] }}>
                                    {t.text}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </Stack>
        </Box>
    );
}

function ToolbarButton({ icon, label, onClick }) {
    return (
        <Button
            onClick={onClick}
            size="small"
            startIcon={icon}
            sx={{ fontSize: 12, fontWeight: 700, px: 1.25, py: 0.6, borderRadius: "6px", bgcolor: "background.paper", border: "1px solid", borderColor: "divider", color: "text.secondary", textTransform: "none" }}
        >
            {label}
        </Button>
    );
}

export { buildSchema, parseInitial, validateRoot };